import { ref, reactive, computed, nextTick } from 'vue';
import { useStore } from 'vuex';
import { cloneDeep, uniq } from 'lodash';

const totalSelectedFilters = ref(0);
const DEFAULT_SORTER = 'newest';

/**
 * URL query keys owned by the main-board filter/sort state.
 *
 * This whitelist is used when search needs to clear card-specific URL state
 * without losing the user's selected board filters. Keeping this list beside
 * the URL <-> filter conversion logic makes it easier to maintain whenever a
 * filter is added, renamed, or removed.
 *
 * Example:
 *   ?selectedTags=MQ==&overdue=true&card_id=123&channel=email
 * becomes:
 *   ?selectedTags=MQ==&overdue=true
 *
 * Bug this prevents:
 *   /board/54?due_today=true&due_tomorrow=true
 * If the user searches from that filtered board, the search flow should not
 * drop due_today/due_tomorrow. Otherwise the URL may become /board/54 or
 * /board/54?card_id=509225, and refreshing would show all cards instead of
 * restoring the user's filtered board results.
 *
 * This is intentionally a whitelist instead of deleting known non-filter keys.
 * That prevents new or unexpected query params from being accidentally carried
 * over as "filter state" during search.
 */
export const BOARD_FILTER_QUERY_KEYS = new Set([
    'selectedStartDate',
    'selectedEndDate',
    'selectedCreationDate',
    'selectedSorter',
    'selectedMilestones',
    'selectedTags',
    'selectedAuthors',
    'selectedAssignees',
    'selectedMembers',
    'selectedExcludeAuthors',
    'selectedExcludeAssignees',
    'selectedExcludeMembers',
    'selectedIssuers',
    'selectedExcludeIssuers',
    'selectedExcludeMilestones',
    'selectedExcludeTags',
    'selectedQuickFilter',
    'selectedQuickSubFilters',
    'overdue',
    'due_today',
    'due_tomorrow',
]);

export function getBoardFilterQueries(routeQuery = {}) {
    return Object.keys(routeQuery).reduce((queries, key) => {
        if (BOARD_FILTER_QUERY_KEYS.has(key)) {
            queries[key] = routeQuery[key];
        }
        return queries;
    }, {});
}

export function useBoardFiltersInURL() {
    const store = useStore();
    /**
    * This stores the deep-copied query of the incoming main-board route. This
    * is what the composable operates on (through the `fromURLToBoardFiltersFunc`
    * function) to set the filters in the mainBoard store module before
    * mounting the main-board component. Its properties are the same as the
    * `storeFilters` variable, but it  depends on which filters were appended
    * to the url.
    */

    let queries = reactive({});
    /**
     * This stores the mutated values of the same object-keys found in the states
     * of the mainBoard store module. Through the `fromBoardFiltersToURLFunc`,
     * the composable produces the final queries from the finalized filters to
     * be appended to the url.
     */

    let storeFilters = reactive({
        selectedStartDate: '',
        selectedEndDate: '',
        selectedCreationStartDate: '',
        selectedCreationEndDate: '',
        selectedSorter: DEFAULT_SORTER,
        selectedMilestones: [],
        selectedTags: [],
        selectedAuthors: [],
        selectedAssignees: [],
        selectedIssuers: [],
        selectedMembers: [],
        selectedExcludeAuthors: [],
        selectedExcludeAssignees: [],
        selectedExcludeIssuers: [],
        selectedExcludeMembers: [],
        selectedExcludeMilestones: [],
        selectedExcludeTags: [],
        selectedQuickFilter: '',
        selectedQuickSubFilters: [],
        selectedDueDatePresets: [],
    });

    /**
     * Cache certain states only
     */
    const computedStates = computed(() => ({
        selectedStartDate: store?.state?.mainBoard?.selectedStartDate,
        selectedEndDate: store?.state?.mainBoard?.selectedEndDate,
        selectedCreationStartDate: store?.state?.mainBoard?.selectedCreationStartDate,
        selectedCreationEndDate: store?.state?.mainBoard?.selectedCreationEndDate,
        selectedSorter: store?.state?.mainBoard?.selectedSorter,
        selectedMilestones: store?.state?.mainBoard?.selectedMilestones,
        selectedTags: store?.state?.mainBoard?.selectedTags,
        selectedAuthors: store?.state?.mainBoard?.selectedAuthors,
        selectedAssignees: store?.state?.mainBoard?.selectedAssignees,
        selectedIssuers: store?.state?.mainBoard?.selectedIssuers,
        selectedMembers: store?.state?.mainBoard?.selectedMembers,
        selectedExcludeAuthors: store?.state?.mainBoard?.selectedExcludeAuthors,
        selectedExcludeAssignees: store?.state?.mainBoard?.selectedExcludeAssignees,
        selectedExcludeIssuers: store?.state?.mainBoard?.selectedExcludeIssuers,
        selectedExcludeMembers: store?.state?.mainBoard?.selectedExcludeMembers,
        selectedExcludeMilestones: store?.state?.mainBoard?.selectedExcludeMilestones,
        selectedExcludeTags: store?.state?.mainBoard?.selectedExcludeTags,
        selectedQuickFilter: store?.state?.mainBoard?.selectedQuickFilter,
        selectedQuickSubFilters: store?.state?.mainBoard?.selectedQuickSubFilters,
        selectedDueDatePresets: store?.state?.mainBoard?.selectedDueDatePresets,
    }));

    const STORE_TO_URL_KEYS = Object.freeze({
        'Overdue': 'overdue',
        'Due Today': 'due_today',
        'Due Tomorrow': 'due_tomorrow',
    });

    const URL_TO_STORE_KEYS = Object.freeze({
        'overdue': 'Overdue',
        'due_today': 'Due Today',
        'due_tomorrow': 'Due Tomorrow',
    });

    const EXCLUDED_QUERIES = ['card_id', 'board_chat', 'search_key',
        'desktop_user_id', 'desktop_employment_id', 'channel',
        'comment_id'
    ];

    const TYPES = Object.freeze({
        MILESTONES: 0,
        TAGS: 1,
        USER_PROFILES: 2,
        DATE: 3,
        BOOL: 4,
        STRING: 5,
        QUICK_FILTER: 6,
        QUICK_SUB_FILTERS: 7,
    });

    const STATE_TYPES = Object.freeze({
        selectedStartDate: TYPES.DATE,
        selectedEndDate: TYPES.DATE,
        selectedCreationStartDate: TYPES.DATE,
        selectedCreationEndDate: TYPES.DATE,
        selectedSorter: TYPES.STRING,
        selectedMilestones: TYPES.MILESTONES,
        selectedTags: TYPES.TAGS,
        selectedAuthors: TYPES.USER_PROFILES,
        selectedAssignees: TYPES.USER_PROFILES,
        selectedIssuers: TYPES.USER_PROFILES,
        selectedMembers: TYPES.USER_PROFILES,
        selectedExcludeAuthors: TYPES.USER_PROFILES,
        selectedExcludeAssignees: TYPES.USER_PROFILES,
        selectedExcludeIssuers: TYPES.USER_PROFILES,
        selectedExcludeMembers: TYPES.USER_PROFILES,
        selectedExcludeMilestones: TYPES.MILESTONES,
        selectedExcludeTags: TYPES.TAGS,
        selectedQuickFilter: TYPES.QUICK_FILTER,
        selectedQuickSubFilters: TYPES.QUICK_SUB_FILTERS,
        selectedDueDatePresets: TYPES.BOOL,
    });

    const MUTATION_HANDLERS = Object.freeze({
        selectedStartDate: 'setSelectedStartDate',
        selectedEndDate: 'setSelectedEndDate',
        selectedCreationStartDate: 'setSelectedCreationStartDate',
        selectedCreationEndDate: 'setSelectedCreationEndDate',
        selectedSorter: 'setSelectedSorter',
        selectedMilestones: 'setSelectedMilestones',
        selectedTags: 'setSelectedTags',
        selectedAuthors: 'setSelectedAuthors',
        selectedAssignees: 'setSelectedAssignees',
        selectedIssuers: 'setSelectedIssuers',
        selectedMembers: 'setSelectedMembers',
        selectedExcludeAuthors: 'setSelectedExcludeAuthors',
        selectedExcludeAssignees: 'setSelectedExcludeAssignees',
        selectedExcludeIssuers: 'setSelectedExcludeIssuers',
        selectedExcludeMembers: 'setSelectedExcludeMembers',
        selectedExcludeMilestones: 'setSelectedExcludeMilestones',
        selectedExcludeTags: 'setSelectedExcludeTags',
        selectedQuickFilter: 'setSelectedQuickFilter',
        selectedQuickSubFilters: 'setselectedQuickSubFilters',
        selectedDueDatePresets: 'toggleSelectedPreset',
    });

    const activeKeys = computed(() => {
        return Object.keys(storeFilters ?? {}).filter((key) => {
            if (key === 'selectedSorter') {
                return storeFilters?.[key] && storeFilters[key] !== DEFAULT_SORTER;
            }
            return !!storeFilters?.[key]?.length;
        }) ?? [];
    });

    function setStoreFilters(storeValue) {
        Object.keys(STATE_TYPES).forEach((key) => {
            if (key === 'selectedDueDatePresets') {
                storeValue[key].forEach(preset => {
                    const urlKey = STORE_TO_URL_KEYS[preset.name];
                    storeFilters[key] = preset.selected
                        ? uniq([...storeFilters[key], urlKey])
                        : storeFilters[key].filter(k => k !== urlKey);
                });
            } else {
                storeFilters[key] = storeValue[key];
            }
        });
    }

    function fromBoardFiltersToURLFunc() {
        setStoreFilters(computedStates.value);
        // array of native data types e.g. int, string, etc
        const strTypes = [
            'selectedStartDate', 'selectedEndDate', 'selectedCreationStartDate', 'selectedCreationEndDate',
            'selectedSorter', 'selectedQuickFilter'];
        const simpleArrTypes = ['selectedQuickSubFilters'];
        const processedQueries = activeKeys.value.reduce((obj, key) => {
            if (strTypes.includes(key)) {
                obj[key] = storeFilters?.[key];
            } else if (key === 'selectedDueDatePresets') {
                storeFilters?.[key].forEach(url_key => {
                    obj[url_key] = true;
                });
            } else if (simpleArrTypes.includes(key)) {
                obj[key] = btoa(storeFilters?.[key]?.join());
            } else {
                obj[key] = btoa(storeFilters?.[key]?.map(item => item.pk)?.toString());
            }
            return obj;
        }, {});
        setTotalSelectedFilters();
        return processedQueries;
    };

    async function fromURLToBoardFiltersFunc({ routeArgs }) {
        const strTypes = [
            'selectedStartDate', 'selectedEndDate', 'selectedCreationStartDate', 'selectedCreationEndDate',
            'selectedSorter', 'selectedQuickFilter'];
        const returnFunc = () => {
            nextTick(() => {
                setStoreFilters(computedStates.value);
                setTotalSelectedFilters();
            });
        };

        Object.assign(queries, cloneDeep(routeArgs.query));
        Object.keys(queries).forEach((key) => {
            if (EXCLUDED_QUERIES.includes(key)) return;
            if (strTypes.includes(key)) {
                queries[key] = {
                    data: queries[key],
                    type: STATE_TYPES[key]
                };
            } else if (URL_TO_STORE_KEYS[key]) {
                queries['selectedDueDatePresets'] = {
                    data: [...(queries?.selectedDueDatePresets?.data ?? []),
                    { url_key: key, val: queries[key] === 'true' }
                    ],
                    type: STATE_TYPES['selectedDueDatePresets']
                };
                delete queries[key];
            } else if (key === 'verified') {
                // Do nothing, this is a special case because we want a person who is going to be
                // verifed to land on the board page.
            } else {
                // array of integers
                queries[key] = {
                    data: atob(queries[key])
                        .split(',')
                        .map((value) => parseInt(value, 10))
                        .filter(Number.isInteger),
                    type: STATE_TYPES[key]
                };
            }
        });
        if (!Object.keys(queries).length) return;

        let userProfilesToFetch = [];
        let response = undefined;
        for (const key of Object.keys(queries)) {
            switch (queries[key].type) {
                case TYPES.DATE:
                case TYPES.STRING:
                case TYPES.QUICK_FILTER:
                case TYPES.QUICK_SUB_FILTERS:
                    store?.commit(`mainBoard/${MUTATION_HANDLERS[key]}`, queries[key].data);
                    break;
                case TYPES.MILESTONES:
                    const milestonesPKs = queries[key].data.toString();
                    try {
                        response = await store.dispatch('board/fetchMilestones', { pk__in: milestonesPKs });
                        const objects = response.objects.map((milestone) => ({
                            ...milestone,
                            label: milestone.title,
                            isChecked: false,
                        }));
                        store?.commit(`mainBoard/${MUTATION_HANDLERS[key]}`, objects);
                    } catch (e) {
                        console.error(e);
                    }
                    break;
                case TYPES.TAGS:
                    const tagsPKs = queries[key].data.toString();
                    try {
                        response = await store.dispatch('tag/fetchBoardTags', {
                            pk__in: tagsPKs,
                            workforce_id: routeArgs.params.workforce_id,
                            boards: routeArgs.params.board_id
                        });
                        const objects = response.map((tag) => ({
                            ...tag,
                            bgColor: tag.background_hex_color,
                            isChecked: false,
                            textColor: tag.text_hex_color
                        }));
                        store?.commit(`mainBoard/${MUTATION_HANDLERS[key]}`, objects);
                    } catch (e) {
                        console.error(e);
                    }
                    break;
                // WE SKIP FETCHING OF USER_PROFILES TYPES HERE SINCE WE WANT TO FETCH ALL
                // DATA IN ONE GO INSTEAD OF PER STATE OBJ (selectedAssignees,
                // selectedAuthors, etc...) THAT CAN LEAD TO DUPLICATE CALL FOR THE SAME DATA
                case TYPES.USER_PROFILES:
                    userProfilesToFetch.push(...queries[key].data);
                    break;
                case TYPES.BOOL:
                    const toSetOptions = queries[key]?.data;
                    for (const obj of toSetOptions) {
                        store?.commit(
                            `mainBoard/${MUTATION_HANDLERS[key]}`,
                            {
                                name: URL_TO_STORE_KEYS[obj['url_key']],
                                selected: obj['val']
                            }
                        );
                    }
                    break;
                default:
                    break;
            }
        }
        if (!userProfilesToFetch.length) returnFunc();

        userProfilesToFetch = uniq(userProfilesToFetch);
        try {
            response = await store.dispatch('company/fetchUserProfiles', {
                object_model: 'board',
                object_model_id: routeArgs.params.board_id,
                pk__in: userProfilesToFetch
            });
            for (const [key, obj] of Object.entries(queries)) {
                if (obj.type !== TYPES.USER_PROFILES) continue;
                let toStore = obj.data;
                toStore = toStore.map((userPk) =>
                    response.find(profile => profile.pk === parseInt(userPk)));
                store?.commit(`mainBoard/${MUTATION_HANDLERS[key]}`, toStore);
            }
        } catch (e) {
            console.error(e);
        }
        returnFunc();
    }

    function setTotalSelectedFilters() {
        const singleCountKeys = ['selectedMilestones', 'selectedTags',
            'selectedAuthors', 'selectedAssignees', 'selectedMembers', 'selectedSorter',
            'selectedDueDatePresets'
        ];
        const baseCount = activeKeys.value.filter(key => singleCountKeys.includes(key)).length;

        const rangeFilters = [
            'selectedStartDate', 'selectedEndDate',
            'selectedCreationStartDate', 'selectedCreationEndDate',
        ].every(key =>
            activeKeys.value.includes(key)) ? 1 : 0;
        totalSelectedFilters.value = baseCount + rangeFilters;
    }

    function resetFiltersInStore({ includeQuickFilters=false } = {}) {
        Object.keys(MUTATION_HANDLERS).forEach((key) => {
            switch (STATE_TYPES[key]) {
                case TYPES.DATE:
                    store?.commit(`mainBoard/${MUTATION_HANDLERS[key]}`, '');
                    break;
                case TYPES.STRING:
                    store?.commit(`mainBoard/${MUTATION_HANDLERS[key]}`, DEFAULT_SORTER);
                    break;
                case TYPES.MILESTONES:
                case TYPES.USER_PROFILES:
                case TYPES.TAGS:
                    store?.commit(`mainBoard/${MUTATION_HANDLERS[key]}`, []);
                    break;
                default:
                    break;
            }
        });
        store?.commit('mainBoard/resetSelectedPresets');
        if (includeQuickFilters) resetQuickFilters();

        totalSelectedFilters.value = 0;
    };

    function resetQuickFilters() {
        store?.commit('mainBoard/selectedQuickFilter', '');
        store?.commit('mainBoard/setselectedQuickSubFilters', []);
    }

    return {
        fromURLToBoardFiltersFunc,
        fromBoardFiltersToURLFunc,
        resetFiltersInStore,
        totalSelectedFilters
    };
};
